import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

/** Framed publishes its column template as a CSS variable, so every Row in one table aligns
 *  without the caller repeating it. */
export function Framed({ cols, children }: { cols: string; children: ReactNode }) {
  return (
    <Box sx={{
      '--cols': cols,
      border: 1, borderColor: 'divider', borderRadius: 1,
      bgcolor: 'background.paper', overflow: 'hidden',
    }}>
      {children}
    </Box>
  );
}

export function Row({ label, title, head, children }: {
  label: ReactNode;
  title?: string;
  head?: boolean;
  children?: ReactNode;
}) {
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: 'var(--cols)', alignItems: 'center',
      columnGap: 1, px: 1, minHeight: 26,
      borderBottom: 1, borderColor: 'divider',
      '&:last-of-type': { borderBottom: 0 },
      ...(head
        ? { bgcolor: 'action.hover' }
        : { '&:hover': { bgcolor: 'action.hover' } }),
    }}>
      <Typography
        variant="caption"
        noWrap
        title={title}
        sx={{ minWidth: 0, fontWeight: head ? 600 : 400, opacity: head ? 1 : 0.85 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export function Num({ head, children }: { head?: boolean; children: ReactNode }) {
  return (
    <Typography variant="caption" sx={{
      textAlign: 'right', whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums', fontWeight: head ? 600 : 400,
    }}>
      {children}
    </Typography>
  );
}

export function SectionTitle({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 0.4 }}>
      {children}
      {hint && (
        <Typography component="span" variant="caption" sx={{ fontWeight: 400, opacity: 0.6 }}>
          {' '}· {hint}
        </Typography>
      )}
    </Typography>
  );
}
